/**
 * ActivityBars — how much a place saw over time, as one bar per bin, so a tile shows the shape
 * of the day rather than only its total. An empty bin keeps a hairline, so a quiet hour reads
 * as quiet rather than as missing.
 */

import type { ReactElement } from 'react';
import React from 'react';

import { cn } from '@/lib/utils';
import { LauncherTileSize } from '@/types/panels';

interface Props {
    /** Oldest first. */
    bins: number[];
    size: LauncherTileSize;
}

const HEIGHT: Record<LauncherTileSize, string> = {
    [LauncherTileSize.Large]: 'h-16 gap-1',
    [LauncherTileSize.Medium]: 'h-8 gap-0.5',
    [LauncherTileSize.Small]: 'h-4 gap-px',
};

/** A bin with anything in it stays taller than the hairline of an empty one. */
const MIN_BAR_PERCENT = 8;

export const ActivityBars = ({ bins, size }: Props): ReactElement => {
    const most = Math.max(1, ...bins);
    return (
        <span className={cn('flex w-full items-end', HEIGHT[size])}>
            {bins.map((count, index) => (
                <span
                    // A bin is its place on the time axis.
                    key={index}
                    className={cn(
                        'min-w-0 flex-1',
                        count === 0
                            ? 'h-px bg-occ-foreground-25'
                            : 'rounded-t-xs bg-occ-brand-600',
                    )}
                    style={count === 0
                        ? undefined
                        : { height: `${Math.max(MIN_BAR_PERCENT, (count / most) * 100)}%` }}
                />
            ))}
        </span>
    );
};
